/**
 * Re-export the shared protocol types from salsa (single source of truth).
 * Synced via: cp ../salsa/src/infra/app/navigation/protocol.ts src/generated/salsa-protocol.ts
 */
import type {
  HostToSalsaMessage,
  SalsaSurface,
  SalsaToHostMessage,
} from '@/generated/salsa-protocol';

export {
  SalsaMessageType,
  SalsaHostRoute,
  type SalsaSurface,
  type SalsaToHostMessage,
  type HostToSalsaMessage,
} from '@/generated/salsa-protocol';

export interface NavigateToMicroproductPayload {
  params?: Record<string, string>;
  productId: string;
}

export interface NavigateToHostPayload {
  params?: Record<string, string>;
  route: string;
}

/** Normalized page-body scroll metrics bridged from the SPA (`BODY_SCROLL`). */
export interface BodyScrollPayload {
  /** Total height of the scrollable content, in px. */
  contentHeight: number;
  /** Current vertical scroll offset from the top, in px. */
  offsetY: number;
  /** Height of the visible scroll viewport, in px. */
  viewportHeight: number;
}

export interface RouteChangedPayload {
  /** Push (false) vs replace (true). Web hosts ignore this — they always
   *  mirror via replace, since the iframe's own pushState already drives the
   *  browser back stack. Native hosts use it to drive their own nav stack. */
  replace: boolean;
  /** Verbatim `window.location.hash` from the microproduct (e.g.
   *  "#/customer/<uuid>/item/<uuid>"). Opaque — the host carries it into its
   *  own shareable URL, never parses it. */
  route: string;
}

export interface MicroProductWebViewRef {
  sendMessage: (message: HostToSalsaMessage) => void;
}

export interface MicroProductWebViewProps {
  onAuthTokenRequest?: () => Promise<string>;
  /** Called with the SPA's page-body scroll metrics (`BODY_SCROLL` postMessage).
   *  The WebView's own scroll events never fire for in-product scrolling — the
   *  SPA's PageLayout scrolls an inner container, not the document — so the SPA
   *  bridges the metrics instead. Lets the host react to in-product scrolling
   *  (bottom-nav hide). Native only: salsa only sends `BODY_SCROLL` to native
   *  WebView hosts, since web hosts render no scroll-linked chrome. */
  onBodyScroll?: (payload: BodyScrollPayload) => void;
  onNavigateToHost?: (payload: NavigateToHostPayload) => void;
  onNavigateToMicroproduct?: (payload: NavigateToMicroproductPayload) => void;
  /** Called when the microproduct's in-product route changes (`ROUTE_CHANGED`).
   *  Lets the host mirror the opaque route into its own shareable URL so deep
   *  links and refresh survive the iframe/WebView boundary. */
  onRouteChanged?: (payload: RouteChangedPayload) => void;
  /** Called when the microproduct requests the host to toggle its sidebar
   *  (via `SIDEBAR_TOGGLE` postMessage). Typically wired to the hosting
   *  experience's sidebar/drawer toggle via a viewport-aware handler. */
  onSidebarToggle?: () => void;
  /** Current host-sidebar state. Pushed to the SPA via `SIDEBAR_STATE`
   *  postMessage on iframe load and whenever the flags change, so the
   *  microproduct can conditionally render its own toggle affordance. */
  sidebarState?: { isCollapsed: boolean; isDrawerLayout: boolean };
  /** Host routes that this version of RA supports. Sent to SPA on load. */
  supportedHostRoutes?: string[];
  /** Fully-constructed URL to the Salsa standalone SPA, including query params */
  uri: string;
}

/** Incoming messages from salsa SPAs — uses the shared protocol types. */
export type SalsaMessage = SalsaToHostMessage;

export interface BuildSalsaUrlOptions {
  businessOrganizationId: string;
  /** Surface context for the embed. Storefront (operator portal) hosts pass
   *  the selected restaurant and surface; sales-hub hosts omit this and the
   *  SPA defaults to the 'sales-hub' surface. */
  embedContext?: {
    restaurantUUID?: string;
    surface?: SalsaSurface;
  };
  /** Arbitrary passthrough params - a microproduct's configured `client_props`
   *  and / or microproductParams. Applied last, so they override the
   *  standard params. */
  extraParams?: Record<string, unknown>;
  forwardedHost: string;
  locale: string;
  platform: 'web' | 'ios' | 'android';
  productId: string;
  /** Opaque in-product route hash to seed the SPA with on load (deep link).
   *  Appended verbatim to the iframe/WebView URL as a hash fragment, where
   *  `useProductRouter` hydrates it on mount. A leading "#" is added if
   *  missing. Root routes ("", "#", "#/") are treated as no seed. */
  routeHash?: string;
  salsaBaseUrl: string;
}

export function buildSalsaUrl({
  businessOrganizationId,
  embedContext,
  extraParams,
  forwardedHost,
  locale,
  platform,
  productId,
  routeHash,
  salsaBaseUrl,
}: BuildSalsaUrlOptions): string {
  const params = new URLSearchParams({
    businessOrganizationId,
    forwardedHost,
    locale,
    platform,
  });
  if (embedContext?.restaurantUUID) {
    params.set('restaurantUUID', embedContext.restaurantUUID);
  }
  if (embedContext?.surface) {
    params.set('surface', embedContext.surface);
  }
  // Guard to a plain object: a malformed URL param (e.g. an un-stringified
  // "[object Object]" string) would otherwise have its characters iterated into
  // junk 0=…&1=… query params.
  if (
    extraParams &&
    typeof extraParams === 'object' &&
    !Array.isArray(extraParams)
  ) {
    Object.entries(extraParams).forEach(([k, v]) => {
      // Coerce primitives to strings and drop invalid values
      if (
        typeof v === 'string' ||
        typeof v === 'number' ||
        typeof v === 'boolean'
      ) {
        params.set(k, String(v));
      }
    });
  }
  const base = `${salsaBaseUrl}/apps/${productId}?${params.toString()}`;
  // Seed the SPA's route via the URL hash. The host stores `mpRoute` bare
  // (no leading '#') to keep its URL clean, so accept any form — '#/x', '/x',
  // or 'x' — and normalize to a single leading '#'. Skip empty / root values
  // (in either '#'-prefixed or bare form) so the common case loads clean.
  const isRoot =
    !routeHash || routeHash === '#' || routeHash === '#/' || routeHash === '/';
  if (!isRoot) {
    return `${base}${routeHash.startsWith('#') ? routeHash : `#${routeHash}`}`;
  }
  return base;
}
